import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import Lozenge from "@atlaskit/lozenge";

import { templateCategorySectionContentfulRelayFragment$key } from "@/__generated_contentful__/templateCategorySectionContentfulRelayFragment.graphql";
import { TemplateType } from "@/content/templates/template.types";

export type TemplateCategorySectionProps = {
    template: templateCategorySectionContentfulRelayFragment$key;
};

const WHITEBOARD_TEMPLATE_ID = "1HlskYfRNciSnQ02SDhCPs";

const templateCategorySectionStyles = css({
    display: "flex",
    flexDirection: "column"
});

const headingStyles = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#505F79",
    fontSize: ".75rem",
    fontWeight: 400,
    letterSpacing: "1px",
    marginBlockEnd: "24px",
    marginBlockStart: "28px"
});

const categoryContainerStyles = css({
    display: "inline-flex",
    height: "32px",
    paddingBlockEnd: "16px"
});

const categoriesColumnStyles = css({
    display: "flex",
    flexDirection: "column"
});

const TemplateCategorySectionRelayFragment = graphql`
    fragment templateCategorySectionContentfulRelayFragment on ProductTemplate {
        templateTypeCollection {
            items {
                sys {
                    id
                }
                templateTypeNameExternal
            }
        }
        categories: categoryCollection {
            items {
                sys {
                    id
                }
                categoryNameExternal
                urlSlug
            }
        }
    }
`;

// eslint-disable-next-line complexity
export const TemplateCategorySection = ({
    template
}: TemplateCategorySectionProps) => {
    const data = useFragment(TemplateCategorySectionRelayFragment, template);
    const categories = data?.categories?.items || [];
    const templateType = data?.templateTypeCollection?.items || [];

    const intl = useIntl();

    const categoryText = intl.formatMessage({
        id: "ConfluenceTemplateMessages.categories.heading",
        defaultMessage: "CATEGORIES"
    });

    const experimentCategories = (categories ?? []).filter(
        // eslint-disable-next-line complexity
        (category) =>
            category?.urlSlug === "recommended" ||
            category?.urlSlug === "project-management" ||
            category?.urlSlug === "software-development-and-it" ||
            category?.urlSlug === "product-management" ||
            category?.urlSlug === "marketing-and-sales" ||
            category?.urlSlug === "design" ||
            category?.urlSlug === "finance-and-operations" ||
            category?.urlSlug === "human-resources"
    );

    const type = templateType?.[0] ?? {
        id: null,
        templateTypeName: null
    };
    const templateTypeKey =
        "sys" in type && type.sys?.id === WHITEBOARD_TEMPLATE_ID
            ? TemplateType.Whiteboard
            : TemplateType.Page;

    const templateTypeLabel =
        "templateTypeNameExternal" in type
            ? type.templateTypeNameExternal ?? TemplateType.Page
            : TemplateType.Page;

    return (
        <section css={templateCategorySectionStyles}>
            <h1 css={headingStyles}>{categoryText}</h1>
            <div css={[categoriesColumnStyles]}>
                {experimentCategories.map((category) => (
                    <div key={category?.sys?.id} css={categoryContainerStyles}>
                        <Lozenge appearance="inprogress">
                            {category?.categoryNameExternal}
                        </Lozenge>
                    </div>
                ))}
                <div key={templateTypeKey} css={categoryContainerStyles}>
                    <Lozenge
                        appearance={
                            templateTypeKey === TemplateType.Page
                                ? "inprogress"
                                : "success"
                        }
                        isBold
                    >
                        <FormattedMessage
                            id="confluence.templates.detail.template.type"
                            defaultMessage={`{templateType} template`}
                            values={{ templateType: templateTypeLabel }}
                        />
                    </Lozenge>
                </div>
            </div>
        </section>
    );
};
